/* Manual pages, timed pages and scrolling share the same original messages.
 * Signup remains outside the moving lane and keeps its native Shopify form. */
(() => {
  if (customElements.get('announcement-strip')) return;

  const focusable = 'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]';

  customElements.define('announcement-strip', class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-ann-viewport]');
      this.track = this.querySelector('[data-ann-track]');
      this.sequence = this.querySelector('[data-ann-sequence]');
      this.pauseButton = this.querySelector('[data-ann-pause]');
      this.signup = this.querySelector('[data-ann-signup]');
      this.signupToggle = this.querySelector('[data-ann-signup-toggle]');
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.sets = [...(this.sequence?.children || [])].filter(item => item.matches('[data-ann-set]'));
      this.hasMessages = this.sets.length > 0;
      this.controls = this.querySelector('[data-ann-controls]');
      this.navigation = this.querySelector('[data-ann-navigation]');
      this.previousButton = this.querySelector('[data-ann-previous]');
      this.nextButton = this.querySelector('[data-ann-next]');
      this.setStatus = this.querySelector('[data-ann-set-status]');
      this.activeIndex = Math.min(this.activeIndex || 0, Math.max(0, this.sets.length - 1));
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = !!this.signup?.querySelector('[data-ann-result]');
      this.hovered = false;
      this.editorSelections = new Set();
      this.editorOpenedSignup = false;
      this.frame = 0;
      this.focusFrame = 0;
      this.cycle = 0;
      this.position = 0;
      this.timer = 0;
      this.inView = true;
      if (this.viewport) this.viewport.hidden = !this.hasMessages;
      this.hidden = !this.hasMessages && !this.signup && !this.sets.length && !this.querySelector('[data-ann-appearance], announcement-corner');
      this.activateSet(this.activeIndex, { announce: false });
      this.previousButton?.addEventListener('click', () => this.activateSet(this.activeIndex - 1), options);
      this.nextButton?.addEventListener('click', () => this.activateSet(this.activeIndex + 1), options);
      this.navigation?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.sets.length - 1 : this.activeIndex + (event.key === 'ArrowRight' ? direction : -direction);
        this.activateSet(index);
      }, options);

      this.onMotion = () => this.rebuild();
      this.reducedMotion.addEventListener('change', this.onMotion);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.sync(); }
      }, options);
      this.addEventListener('pointerleave', () => { this.hovered = false; this.sync(); }, options);
      this.viewport?.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse') { this.userPaused = true; this.sync(); }
      }, options);
      this.addEventListener('focusin', event => {
        // A pointer can focus a repeated link, even though copies are skipped by Tab.
        event.target.closest('[data-ann-copy]')?.removeAttribute('aria-hidden');
        this.sync();
        if (this.viewport?.contains(event.target) && event.target !== this.viewport) {
          this.reveal(event.target.closest('[data-ann-item]') || event.target, event.target);
        }
      }, options);
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (!this.isConnected) return;
        this.track?.querySelectorAll('[data-ann-copy]').forEach(copy => {
          if (!copy.contains(document.activeElement)) copy.setAttribute('aria-hidden', 'true');
        });
        if (this.signup?.open && !this.signup.contains(document.activeElement) && !this.editorOpenedSignup) this.closeSignup(false);
        this.sync();
      }), options);
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.sync();
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);

      if (this.signup) {
        this.signupToggle?.addEventListener('click', () => {
          this.editorOpenedSignup = false;
          this.focusOnOpen = !this.signup.open;
        }, options);
        this.signup.addEventListener('pointerdown', () => { this.editorOpenedSignup = false; }, options);
        this.signup.addEventListener('keydown', event => {
          this.editorOpenedSignup = false;
          if (event.key !== 'Escape' || !this.signup.open) return;
          event.preventDefault();
          event.stopPropagation();
          this.closeSignup(true);
        }, options);
        this.signup.addEventListener('toggle', () => {
          if (this.signup.open && this.focusOnOpen) this.focusSignup();
          if (!this.signup.open) {
            cancelAnimationFrame(this.focusFrame);
            this.editorOpenedSignup = false;
          }
          this.focusOnOpen = false;
          this.sync();
        }, options);
        this.signup.querySelector('[data-ann-signup-close]')?.addEventListener('click', () => this.closeSignup(true), options);
        document.addEventListener('pointerdown', event => {
          if (this.signup.open && !event.composedPath().includes(this.signup)) this.closeSignup(false);
        }, options);
        if (this.signup.querySelector('[data-ann-result]')) this.openSignup(true);
      }

      for (const eventName of ['shopify:block:select', 'shopify:section:select']) {
        document.addEventListener(eventName, event => {
          if (!this.section.contains(event.target)) return;
          this.editorSelections.add(this.editorKey(event));
          const isBlock = event.type === 'shopify:block:select';
          const selectedSet = event.target.closest('[data-ann-set]');
          if (isBlock && selectedSet && this.sets.includes(selectedSet)) this.activateSet(this.sets.indexOf(selectedSet), { announce: false });
          if (isBlock && this.signup && (event.target.contains(this.signup) || this.signup.contains(event.target))) {
            if (!this.signup.open) {
              this.editorOpenedSignup = this.editorKey(event);
              this.openSignup(false);
            }
          } else {
            const item = event.target.closest('[data-ann-item]');
            if (item && this.viewport?.contains(item)) this.reveal(item, item);
          }
          this.sync();
        }, options);
      }
      for (const eventName of ['shopify:block:deselect', 'shopify:section:deselect']) {
        document.addEventListener(eventName, event => {
          if (!this.section.contains(event.target)) return;
          const key = this.editorKey(event);
          this.editorSelections.delete(key);
          if (this.editorOpenedSignup && (this.editorOpenedSignup === key || !this.editorSelections.size)) this.closeSignup(false);
          this.sync();
        }, options);
      }

      this.observer = new ResizeObserver(() => {
        this.section.style.setProperty('--ann-height', `${this.getBoundingClientRect().height}px`);
        if (!this.hasMessages) return;
        const width = this.viewport.clientWidth;
        const cycle = this.sequence.getBoundingClientRect().width;
        if (width !== this.measuredWidth || cycle !== this.cycle) this.rebuild();
      });
      this.observer.observe(this);
      if (this.sequence) this.observer.observe(this.sequence);
      this.visibility = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.sync();
      });
      this.visibility.observe(this);
      this.settings = new MutationObserver(() => { this.stopCycle(); this.rebuild(); });
      this.settings.observe(this, { attributes: true, attributeFilter: ['data-mode', 'data-cycle-delay', 'data-show-arrows', 'data-speed', 'data-motion'] });
      this.setAttribute('data-ann-ready', '');
      this.rebuild();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.focusFrame);
      this.frame = 0;
      this.focusFrame = 0;
      this.stopCycle();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      this.settings?.disconnect();
      this.controller?.abort();
      this.reducedMotion?.removeEventListener('change', this.onMotion);
    }

    editorKey(event) {
      const block = event.type.startsWith('shopify:block:');
      return `${block ? 'block' : 'section'}:${event.detail?.blockId || event.detail?.sectionId || event.target.id || ''}`;
    }

    activateSet(index, { announce = true } = {}) {
      if (!this.sets.length) return;
      const next = ((index % this.sets.length) + this.sets.length) % this.sets.length;
      const previous = this.sets[this.activeIndex];
      const selected = this.sets[next];
      if (previous !== selected && this.dataset.mode !== 'scroll') {
        // A programmatic switch must not strand focus inside a hidden set.
        if (previous?.contains(document.activeElement)) {
          const target = !this.navigation?.hidden ? this.nextButton : this.viewport;
          target?.focus({ preventScroll: true });
        }
      }
      this.activeIndex = next;
      this.dataset.activeSet = String(next + 1);
      this.updatePages();
      if (announce && this.setStatus) this.setStatus.textContent = `${next + 1} of ${this.sets.length}: ${selected.dataset.annSetName}`;
      this.stopCycle();
      this.sync();
    }

    updatePages() {
      this.sets.forEach((set, index) => {
        const inactive = this.dataset.mode !== 'scroll' && index !== this.activeIndex;
        set.hidden = false;
        set.inert = inactive;
        set.toggleAttribute('data-ann-inactive', inactive);
        if (inactive) set.setAttribute('aria-hidden', 'true');
        else set.removeAttribute('aria-hidden');
      });
    }

    openSignup(focus) {
      if (!this.signup) return;
      this.focusOnOpen = focus;
      this.signup.open = true;
      if (focus) this.focusSignup();
      this.sync();
    }

    focusSignup() {
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.signup?.open) return;
        const target = this.signup.querySelector('[data-ann-result]') || this.signup.querySelector('input[type="email"]');
        if (!target) return;
        if (target.hasAttribute('data-ann-result')) target.tabIndex = -1;
        target.focus({ preventScroll: true });
      });
    }

    closeSignup(restoreFocus) {
      if (!this.signup?.open) return;
      cancelAnimationFrame(this.focusFrame);
      this.focusOnOpen = false;
      this.editorOpenedSignup = false;
      this.signup.open = false;
      if (restoreFocus) this.signupToggle?.focus({ preventScroll: true });
      this.sync();
    }

    rebuild() {
      if (!this.hasMessages) { this.sync(); return; }
      // Never discard focus when a copied link vanishes on resize or reduced motion.
      // Signup lives outside the track, so rebuilding cannot touch its draft.
      const focusedCopy = document.activeElement?.closest('[data-ann-copy]');
      let originalFocus;
      if (focusedCopy && this.track.contains(focusedCopy)) {
        const index = [...focusedCopy.querySelectorAll(focusable)].indexOf(document.activeElement);
        originalFocus = [...this.sequence.querySelectorAll(focusable)][index] || this.viewport;
        originalFocus.focus({ preventScroll: true });
      }
      // Removing the copies can temporarily clamp scrollLeft to zero when
      // the originals are shorter than the viewport. Keep the current phase.
      const position = this.viewport.scrollLeft;
      this.track.querySelectorAll('[data-ann-copy]').forEach(copy => copy.remove());
      // A mode change keeps a focused original message visible.
      const focusedSet = this.sets.findIndex(set => set.contains(document.activeElement));
      if (focusedSet !== -1) this.activeIndex = focusedSet;
      this.updatePages();
      this.cycle = this.sequence.getBoundingClientRect().width;
      this.measuredWidth = this.viewport.clientWidth;
      if (this.dataset.mode === 'scroll' && !this.reducedMotion.matches && this.cycle > 0) {
        const copies = Math.ceil(this.measuredWidth / this.cycle) + 1;
        for (let i = 0; i < copies; i++) {
          const copy = this.sequence.cloneNode(true);
          copy.removeAttribute('data-ann-sequence');
          copy.removeAttribute('data-ann-sets');
          copy.setAttribute('data-ann-copy', '');
          copy.setAttribute('aria-hidden', 'true');
          for (const node of [copy, ...copy.querySelectorAll('[id], [data-shopify-editor-block], [data-shopify-editor-section]')]) {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
            node.removeAttribute('data-shopify-editor-section');
          }
          copy.querySelectorAll(focusable).forEach(node => { node.tabIndex = -1; });
          this.track.appendChild(copy);
        }
      }
      this.position = this.cycle ? position % this.cycle : 0;
      if (this.dataset.mode !== 'scroll') this.position = 0;
      this.viewport.scrollLeft = this.position;
      if (originalFocus) this.reveal(originalFocus, originalFocus);
      this.sync();
    }

    reveal(item, fallback) {
      if (!this.viewport) return;
      const viewport = this.viewport.getBoundingClientRect();
      let rect = item.getBoundingClientRect();
      if (rect.width > viewport.width - 16) rect = fallback.getBoundingClientRect();
      if (rect.left < viewport.left + 8) this.viewport.scrollLeft += rect.left - viewport.left - 8;
      else if (rect.right > viewport.right - 8) this.viewport.scrollLeft += rect.right - viewport.right + 8;
      this.position = this.viewport.scrollLeft;
    }

    sync() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      const reduced = this.reducedMotion.matches;
      const scrolling = this.hasMessages && this.dataset.mode === 'scroll' && !reduced && this.cycle > 0;
      const cycling = this.sets.length > 1 && this.dataset.mode === 'cycle' && !reduced;
      const moving = scrolling || cycling;
      if (this.navigation) this.navigation.hidden = this.sets.length < 2 || this.dataset.mode === 'scroll' ||
        (this.dataset.mode === 'cycle' && this.dataset.showArrows === 'false' && !reduced);
      if (this.pauseButton) {
        this.pauseButton.hidden = !moving;
        this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
        this.pauseButton.setAttribute('aria-label', this.userPaused ? 'Play announcements' : 'Pause announcements');
      }
      if (this.controls) this.controls.hidden = this.navigation?.hidden !== false && this.pauseButton?.hidden !== false;
      const focused = this.contains(document.activeElement) && document.activeElement !== this.pauseButton;
      if (!moving || this.userPaused || this.hovered || focused || this.signup?.open || this.querySelector('announcement-corner details[open]') || this.editorSelections.size || document.hidden || !this.inView) {
        this.stopCycle();
        return;
      }
      if (cycling) {
        if (!this.timer) {
          const delay = Math.max(3, Math.min(30, Number(this.dataset.cycleDelay) || 6)) * 1000;
          this.timer = setTimeout(() => {
            this.timer = 0;
            // Automatic changes stay quiet for screen readers. Manual steps
            // announce the selected page and always restart the full delay.
            this.activateSet(this.activeIndex + 1, { announce: false });
          }, delay);
        }
        return;
      }
      this.stopCycle();
      this.position = this.viewport.scrollLeft;
      this.lastTime = 0;
      const tick = time => {
        if (this.lastTime) {
          const step = Math.min(time - this.lastTime, 64) * (Number(this.dataset.speed) || 25) / 1000;
          const direction = this.dataset.motion === 'right' ? -1 : 1;
          this.position = ((this.position + step * direction) % this.cycle + this.cycle) % this.cycle;
          this.viewport.scrollLeft = this.position;
        }
        this.lastTime = time;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }

    stopCycle() {
      clearTimeout(this.timer);
      this.timer = 0;
    }
  });
})();
